/**
 * Gemelo en JS de app/Services/Police/MrzNationality.php (mantener sincronizados).
 *
 * Dos trabajos:
 *  1. `toIso3()` — normaliza lo que entrega el escáner de documentos: códigos
 *     MRZ que no son ISO3 ("D" = Alemania), ISO2, nombres de país y lecturas
 *     con un "?" literal donde el OCR no reconoció un carácter.
 *  2. `flagUrl()` — devuelve la ruta de la bandera en /public/Flags, que están
 *     nombradas por ISO2.
 *
 * ⚠️ El motivo de existir de la parte de banderas: por todo el panel se hacía
 * `code.substr(0, 2)` para pasar de ISO3 al nombre del fichero. Acierta por
 * casualidad en muchos países (ESP→ES, ITA→IT) y en otros pinta LA BANDERA DE
 * OTRO PAÍS: CHL y CHN→"CH" (Suiza), MEX→"ME" (Montenegro), PRT→"PR" (Puerto
 * Rico), IRL→"IR" (Irán), AUT→"AU" (Australia); y deja hueco en DNK→"DN",
 * SWE→"SW", KOR→"KO", POL→"PO", TUN→"TU".
 */

/** Alias MRZ/ICAO, códigos deportivos y correcciones de escáner verificadas. */
const ALIASES = {
  D: "DEU", GBD: "GBR", GBN: "GBR", GBO: "GBR",
  GBP: "GBR", GBS: "GBR", UK: "GBR", RKS: "XKX",
  KOS: "XKX", EL: "GRC", SUI: "CHE", SWI: "CHE",
  GER: "DEU", NED: "NLD", DEN: "DNK", GRE: "GRC",
  CRO: "HRV", POR: "PRT", SLO: "SVN", BUL: "BGR",
  LAT: "LVA", RSA: "ZAF", NLK: "NLD", NIU: "NLD",
  NDL: "NLD", HOL: "NLD", "?NK": "DNK", "?SP": "ESP",
  E5P: "ESP", I1A: "ITA", "?IA": "ITA", "C?I": "CRI",
  "?OU": "ROU", AR9: "MAR",
};

/** Nombres de país escritos en el campo del código. */
const NAME_TO_ISO3 = {
  AFGANISTAN: "AFG", ALEMANIA: "DEU", AMERICA: "USA",
  ANGOLA: "AGO", ARGENTINA: "ARG", ARMENIA: "ARM",
  BRASIL: "BRA", COLOMBIA: "COL", DINAMARCA: "DNK",
  DENMARK: "DNK", ESPANA: "ESP", "ESPAÑA": "ESP",
  SPAIN: "ESP", FRANCIA: "FRA", FRANCE: "FRA",
  ITALIA: "ITA", ITALY: "ITA", MEXICO: "MEX",
  PERU: "PER", PORTUGAL: "PRT", "REINO UNIDO": "GBR",
  VENEZUELA: "VEN",
};

/** ISO 3166-1 alpha-2 → alpha-3. */
const ISO2_TO_ISO3 = {
  AD: "AND", AE: "ARE", AF: "AFG", AG: "ATG", AI: "AIA", AL: "ALB",
  AM: "ARM", AO: "AGO", AQ: "ATA", AR: "ARG", AS: "ASM", AT: "AUT",
  AU: "AUS", AW: "ABW", AX: "ALA", AZ: "AZE", BA: "BIH", BB: "BRB",
  BD: "BGD", BE: "BEL", BF: "BFA", BG: "BGR", BH: "BHR", BI: "BDI",
  BJ: "BEN", BL: "BLM", BM: "BMU", BN: "BRN", BO: "BOL", BQ: "BES",
  BR: "BRA", BS: "BHS", BT: "BTN", BV: "BVT", BW: "BWA", BY: "BLR",
  BZ: "BLZ", CA: "CAN", CC: "CCK", CD: "COD", CF: "CAF", CG: "COG",
  CH: "CHE", CI: "CIV", CK: "COK", CL: "CHL", CM: "CMR", CN: "CHN",
  CO: "COL", CR: "CRI", CU: "CUB", CV: "CPV", CW: "CUW", CX: "CXR",
  CY: "CYP", CZ: "CZE", DE: "DEU", DJ: "DJI", DK: "DNK", DM: "DMA",
  DO: "DOM", DZ: "DZA", EC: "ECU", EE: "EST", EG: "EGY", EH: "ESH",
  ER: "ERI", ES: "ESP", ET: "ETH", FI: "FIN", FJ: "FJI", FK: "FLK",
  FM: "FSM", FO: "FRO", FR: "FRA", GA: "GAB", GB: "GBR", GD: "GRD",
  GE: "GEO", GF: "GUF", GG: "GGY", GH: "GHA", GI: "GIB", GL: "GRL",
  GM: "GMB", GN: "GIN", GP: "GLP", GQ: "GNQ", GR: "GRC", GS: "SGS",
  GT: "GTM", GU: "GUM", GW: "GNB", GY: "GUY", HK: "HKG", HM: "HMD",
  HN: "HND", HR: "HRV", HT: "HTI", HU: "HUN", ID: "IDN", IE: "IRL",
  IL: "ISR", IM: "IMN", IN: "IND", IO: "IOT", IQ: "IRQ", IR: "IRN",
  IS: "ISL", IT: "ITA", JE: "JEY", JM: "JAM", JO: "JOR", JP: "JPN",
  KE: "KEN", KG: "KGZ", KH: "KHM", KI: "KIR", KM: "COM", KN: "KNA",
  KP: "PRK", KR: "KOR", KW: "KWT", KY: "CYM", KZ: "KAZ", LA: "LAO",
  LB: "LBN", LC: "LCA", LI: "LIE", LK: "LKA", LR: "LBR", LS: "LSO",
  LT: "LTU", LU: "LUX", LV: "LVA", LY: "LBY", MA: "MAR", MC: "MCO",
  MD: "MDA", ME: "MNE", MF: "MAF", MG: "MDG", MH: "MHL", MK: "MKD",
  ML: "MLI", MM: "MMR", MN: "MNG", MO: "MAC", MP: "MNP", MQ: "MTQ",
  MR: "MRT", MS: "MSR", MT: "MLT", MU: "MUS", MV: "MDV", MW: "MWI",
  MX: "MEX", MY: "MYS", MZ: "MOZ", NA: "NAM", NC: "NCL", NE: "NER",
  NF: "NFK", NG: "NGA", NI: "NIC", NL: "NLD", NO: "NOR", NP: "NPL",
  NR: "NRU", NU: "NIU", NZ: "NZL", OM: "OMN", PA: "PAN", PE: "PER",
  PF: "PYF", PG: "PNG", PH: "PHL", PK: "PAK", PL: "POL", PM: "SPM",
  PN: "PCN", PR: "PRI", PS: "PSE", PT: "PRT", PW: "PLW", PY: "PRY",
  QA: "QAT", RE: "REU", RO: "ROU", RS: "SRB", RU: "RUS", RW: "RWA",
  SA: "SAU", SB: "SLB", SC: "SYC", SD: "SDN", SE: "SWE", SG: "SGP",
  SH: "SHN", SI: "SVN", SJ: "SJM", SK: "SVK", SL: "SLE", SM: "SMR",
  SN: "SEN", SO: "SOM", SR: "SUR", SS: "SSD", ST: "STP", SV: "SLV",
  SX: "SXM", SY: "SYR", SZ: "SWZ", TC: "TCA", TD: "TCD", TF: "ATF",
  TG: "TGO", TH: "THA", TJ: "TJK", TK: "TKL", TL: "TLS", TM: "TKM",
  TN: "TUN", TO: "TON", TR: "TUR", TT: "TTO", TV: "TUV", TW: "TWN",
  TZ: "TZA", UA: "UKR", UG: "UGA", UM: "UMI", US: "USA", UY: "URY",
  UZ: "UZB", VA: "VAT", VC: "VCT", VE: "VEN", VG: "VGB", VI: "VIR",
  VN: "VNM", VU: "VUT", WF: "WLF", WS: "WSM", XK: "XKX", YE: "YEM",
  YT: "MYT", ZA: "ZAF", ZM: "ZMB", ZW: "ZWE",
};

/** ISO3 → ISO2, que es como se llaman los ficheros de /public/Flags. */
const ISO3_TO_ISO2 = {
  AND: "AD", ARE: "AE", AFG: "AF", ATG: "AG", AIA: "AI", ALB: "AL",
  ARM: "AM", AGO: "AO", ATA: "AQ", ARG: "AR", ASM: "AS", AUT: "AT",
  AUS: "AU", ABW: "AW", ALA: "AX", AZE: "AZ", BIH: "BA", BRB: "BB",
  BGD: "BD", BEL: "BE", BFA: "BF", BGR: "BG", BHR: "BH", BDI: "BI",
  BEN: "BJ", BLM: "BL", BMU: "BM", BRN: "BN", BOL: "BO", BES: "BQ",
  BRA: "BR", BHS: "BS", BTN: "BT", BVT: "BV", BWA: "BW", BLR: "BY",
  BLZ: "BZ", CAN: "CA", CCK: "CC", COD: "CD", CAF: "CF", COG: "CG",
  CHE: "CH", CIV: "CI", COK: "CK", CHL: "CL", CMR: "CM", CHN: "CN",
  COL: "CO", CRI: "CR", CUB: "CU", CPV: "CV", CUW: "CW", CXR: "CX",
  CYP: "CY", CZE: "CZ", DEU: "DE", DJI: "DJ", DNK: "DK", DMA: "DM",
  DOM: "DO", DZA: "DZ", ECU: "EC", EST: "EE", EGY: "EG", ESH: "EH",
  ERI: "ER", ESP: "ES", ETH: "ET", FIN: "FI", FJI: "FJ", FLK: "FK",
  FSM: "FM", FRO: "FO", FRA: "FR", GAB: "GA", GBR: "GB", GRD: "GD",
  GEO: "GE", GUF: "GF", GGY: "GG", GHA: "GH", GIB: "GI", GRL: "GL",
  GMB: "GM", GIN: "GN", GLP: "GP", GNQ: "GQ", GRC: "GR", SGS: "GS",
  GTM: "GT", GUM: "GU", GNB: "GW", GUY: "GY", HKG: "HK", HMD: "HM",
  HND: "HN", HRV: "HR", HTI: "HT", HUN: "HU", IDN: "ID", IRL: "IE",
  ISR: "IL", IMN: "IM", IND: "IN", IOT: "IO", IRQ: "IQ", IRN: "IR",
  ISL: "IS", ITA: "IT", JEY: "JE", JAM: "JM", JOR: "JO", JPN: "JP",
  KEN: "KE", KGZ: "KG", KHM: "KH", KIR: "KI", COM: "KM", KNA: "KN",
  PRK: "KP", KOR: "KR", KWT: "KW", CYM: "KY", KAZ: "KZ", LAO: "LA",
  LBN: "LB", LCA: "LC", LIE: "LI", LKA: "LK", LBR: "LR", LSO: "LS",
  LTU: "LT", LUX: "LU", LVA: "LV", LBY: "LY", MAR: "MA", MCO: "MC",
  MDA: "MD", MNE: "ME", MAF: "MF", MDG: "MG", MHL: "MH", MKD: "MK",
  MLI: "ML", MMR: "MM", MNG: "MN", MAC: "MO", MNP: "MP", MTQ: "MQ",
  MRT: "MR", MSR: "MS", MLT: "MT", MUS: "MU", MDV: "MV", MWI: "MW",
  MEX: "MX", MYS: "MY", MOZ: "MZ", NAM: "NA", NCL: "NC", NER: "NE",
  NFK: "NF", NGA: "NG", NIC: "NI", NLD: "NL", NOR: "NO", NPL: "NP",
  NRU: "NR", NIU: "NU", NZL: "NZ", OMN: "OM", PAN: "PA", PER: "PE",
  PYF: "PF", PNG: "PG", PHL: "PH", PAK: "PK", POL: "PL", SPM: "PM",
  PCN: "PN", PRI: "PR", PSE: "PS", PRT: "PT", PLW: "PW", PRY: "PY",
  QAT: "QA", REU: "RE", ROU: "RO", SRB: "RS", RUS: "RU", RWA: "RW",
  SAU: "SA", SLB: "SB", SYC: "SC", SDN: "SD", SWE: "SE", SGP: "SG",
  SHN: "SH", SVN: "SI", SJM: "SJ", SVK: "SK", SLE: "SL", SMR: "SM",
  SEN: "SN", SOM: "SO", SUR: "SR", SSD: "SS", STP: "ST", SLV: "SV",
  SXM: "SX", SYR: "SY", SWZ: "SZ", TCA: "TC", TCD: "TD", ATF: "TF",
  TGO: "TG", THA: "TH", TJK: "TJ", TKL: "TK", TLS: "TL", TKM: "TM",
  TUN: "TN", TON: "TO", TUR: "TR", TTO: "TT", TUV: "TV", TWN: "TW",
  TZA: "TZ", UKR: "UA", UGA: "UG", UMI: "UM", USA: "US", URY: "UY",
  UZB: "UZ", VAT: "VA", VCT: "VC", VEN: "VE", VGB: "VG", VIR: "VI",
  VNM: "VN", VUT: "VU", WLF: "WF", WSM: "WS", XKX: "XK", YEM: "YE",
  MYT: "YT", ZAF: "ZA", ZMB: "ZM", ZWE: "ZW",
};

/** Normaliza cualquier lectura a ISO3. Devuelve "" si no hay nada. */
export function toIso3(code) {
  const c = String(code == null ? "" : code).trim().toUpperCase();
  if (!c) return "";
  if (ALIASES[c]) return ALIASES[c];
  if (c.length === 2 && ISO2_TO_ISO3[c]) return ISO2_TO_ISO3[c];
  if (NAME_TO_ISO3[c]) return NAME_TO_ISO3[c];
  return c;
}

/** ISO2 para el nombre del fichero de bandera. null si no se reconoce. */
export function flagCode(code) {
  const iso3 = toIso3(code);
  if (!iso3) return null;
  if (ISO3_TO_ISO2[iso3]) return ISO3_TO_ISO2[iso3];
  // Ya venía en ISO2 y es uno que conocemos.
  if (iso3.length === 2 && ISO2_TO_ISO3[iso3]) return iso3;
  return null;
}

/**
 * Ruta de la bandera, o null si el código no se reconoce —así el llamante
 * puede no pintar el <img> en vez de dejar un icono roto.
 */
export function flagUrl(code) {
  const iso2 = flagCode(code);
  return iso2 ? `/Flags/${iso2}.png` : null;
}

/** Bandera del primer código utilizable de un huésped. */
export function guestFlagUrl(cliente) {
  if (!cliente) return null;
  return flagUrl(cliente.code) || flagUrl(cliente.pais_code) || null;
}

export default { toIso3, flagCode, flagUrl, guestFlagUrl };
